import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  BoxProps,
  ToggleButton as MuiToggleButton,
  useTheme,
  styled,
} from "@mui/material";
import { DateRangeButton } from "@components/DatePickers";

const ToggleButton = styled(MuiToggleButton, {
  shouldForwardProp: (prop) => prop !== "paddingSize",
})<{ paddingSize: Sizes }>`
  font-size: 14px;
  line-height: 22px;
  border: none;
  font-weight: normal;
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: ${({ theme, paddingSize }) =>
    theme.spacing(1.5, paddingSizes[paddingSize])};
  text-transform: unset;

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.neutralDark};
    font-weight: 500;
  }
`;

const fullDefaultIntervalTypes: IntervalType[] = [
  "oneWeek",
  "oneMonth",
  "YTD",
  "oneYear",
  "threeYears",
  "fiveYears",
  "sinceBeginning",
  "custom",
];

const compactDefaultIntervalTypes: IntervalType[] = [
  "oneWeek",
  "oneMonth",
  "oneYear",
  "fiveYears",
  "custom",
];

export const performanceIntervalTypes: IntervalType[] = [
  "oneWeek",
  "oneMonth",
  "YTD",
  "oneYear",
  "threeYears",
  "sinceBeginning",
  "custom",
];

const Container = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1, 0)};
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    gap: ${({ theme }) => theme.spacing(1)};
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    flex-grow: 1;
  }
`;

const paddingSizes = {
  small: 2,
  medium: 3,
};

type Sizes = "small" | "medium";

export interface RangeSelectorProps extends Omit<BoxProps, "onSelect"> {
  className?: string;
  intervalType?: IntervalType;
  range?: RangeType;
  compact?: boolean;
  intervalTypes?: IntervalType[];
  compactIntervalTypes?: IntervalType[];
  onSelect: (type: IntervalType) => void;
  onCustomSelect?: (from: Date, to: Date) => void;
  disabledIntervalTypes?: IntervalType[];
  disablePortal?: boolean;
  size?: Sizes;
  allowFutureInCustom?: boolean;
}

export const RangeSelector = ({
  onSelect,
  intervalType,
  range,
  className,
  compact,
  intervalTypes = fullDefaultIntervalTypes,
  compactIntervalTypes = compactDefaultIntervalTypes,
  onCustomSelect,
  disabledIntervalTypes,
  disablePortal,
  size = "medium",
  allowFutureInCustom = false,
  ...rest
}: RangeSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClick = (type: IntervalType) => {
    return onSelect(type);
  };

  const handleCustomSubmit = (from: Date, to?: Date) => {
    return onCustomSelect && onCustomSelect(from, to!);
  };

  const intervals = compact ? compactIntervalTypes : intervalTypes;

  return (
    <Container className={className} {...rest}>
      {intervals.map((type) =>
        type === "custom" ? (
          <DateRangeButton
            key="custom"
            data-testid={`button-${type}`}
            onChange={handleCustomSubmit}
            onReset={() => handleClick("YTD")}
            allowFuture={allowFutureInCustom}
            active={intervalType === "custom"}
            sx={{
              color:
                intervalType === "custom" ? "primary.main" : "icon.primary",
              backgroundColor:
                intervalType === "custom"
                  ? "background.default"
                  : "transparent",
              border:
                intervalType === "custom"
                  ? `1px solid ${theme.palette.border.secondary}`
                  : "none",
              height: "32px",
            }}
            from={range?.startDate}
            to={range?.endDate}
            disabled={disabledIntervalTypes?.includes(type)}
            disablePortal={disablePortal}
          />
        ) : (
          <ToggleButton
            data-testid={`button-${type}`}
            size="small"
            paddingSize={size}
            key={type}
            value={type}
            selected={type === intervalType}
            onClick={() => {
              handleClick(type);
            }}
            disabled={disabledIntervalTypes?.includes(type)}
          >
            {type === "currentYear"
              ? new Date().getFullYear()
              : t(
                  `general.ranges.rangeSelector.intervalSelector.compact.${type}`
                )}
          </ToggleButton>
        )
      )}
    </Container>
  );
};
